import { useEffect, useRef } from "react";

export const getReconnectDelay = (attempt, jitter = Math.random()) => (
  Math.min(8000, 750 * (2 ** Math.max(0, attempt))) + Math.floor(jitter * 250)
);

/**
 * Keeps a lightweight command socket alive across mobile network handoffs,
 * background throttling, and half-open proxy connections.
 */
export const useResilientWebSocket = ({
  url,
  enabled = true,
  socketRef,
  onMessage,
  onOpen,
  onClose,
  heartbeatMs = 10000,
  staleAfterMs = 30000,
}) => {
  const onMessageRef = useRef(onMessage);
  const onOpenRef = useRef(onOpen);
  const onCloseRef = useRef(onClose);

  useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);
  useEffect(() => { onOpenRef.current = onOpen; }, [onOpen]);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!enabled || !url) return undefined;

    let socket;
    let pingInterval;
    let reconnectTimer;
    let reconnectAttempt = 0;
    let lastPongAt = Date.now();
    let cancelled = false;

    const scheduleReconnect = () => {
      if (cancelled || reconnectTimer) return;
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        connect();
      }, getReconnectDelay(reconnectAttempt));
      reconnectAttempt += 1;
    };

    function connect() {
      if (
        cancelled
        || !navigator.onLine
        || socket?.readyState === WebSocket.CONNECTING
        || socket?.readyState === WebSocket.OPEN
      ) return;

      const currentSocket = new WebSocket(url);
      socket = currentSocket;
      if (socketRef) socketRef.current = currentSocket;

      currentSocket.onopen = () => {
        if (socket !== currentSocket) return;
        reconnectAttempt = 0;
        lastPongAt = Date.now();
        window.clearInterval(pingInterval);
        onOpenRef.current?.();
        pingInterval = window.setInterval(() => {
          if (currentSocket.readyState !== WebSocket.OPEN) return;
          if (Date.now() - lastPongAt > staleAfterMs) {
            currentSocket.close(4000, "Heartbeat timed out");
            return;
          }
          currentSocket.send("ping");
        }, heartbeatMs);
      };

      currentSocket.onmessage = (message) => {
        if (message.data === "pong") {
          lastPongAt = Date.now();
          return;
        }
        onMessageRef.current?.(message);
      };

      currentSocket.onclose = (closeEvent) => {
        if (socket !== currentSocket) return;
        window.clearInterval(pingInterval);
        if (socketRef?.current === currentSocket) socketRef.current = null;
        socket = null;
        onCloseRef.current?.(closeEvent);
        scheduleReconnect();
      };

      currentSocket.onerror = () => currentSocket.close();
    }

    const resumeConnection = () => {
      if (navigator.onLine && document.visibilityState === "visible" && socket?.readyState !== WebSocket.OPEN) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = null;
        connect();
      }
    };

    connect();
    window.addEventListener("online", resumeConnection);
    document.addEventListener("visibilitychange", resumeConnection);

    return () => {
      cancelled = true;
      window.removeEventListener("online", resumeConnection);
      document.removeEventListener("visibilitychange", resumeConnection);
      window.clearInterval(pingInterval);
      window.clearTimeout(reconnectTimer);
      const closingSocket = socket;
      socket = null;
      if (socketRef?.current === closingSocket) socketRef.current = null;
      closingSocket?.close();
    };
  }, [enabled, heartbeatMs, socketRef, staleAfterMs, url]);
};
